import { sortOrganisations } from "@/utils/organisationSort";

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { SimplifiedUserRole } from "@/types/user";
import { UserRoleRow } from "@/components/users/UserRoleRow";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSimpleRole } from "@/hooks/use-simple-role";

export default function UserRoles() {
  const [selectedOrg, setSelectedOrg] = useState<string>("all");
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const { user } = useAuth();
  const { role: viewerRole, isSystemAdmin } = useSimpleRole();
  const isOrgAdmin = viewerRole === 'admin' && !isSystemAdmin;

  const { data: userRoles, isLoading } = useQuery({
    queryKey: ["user-roles", isOrgAdmin ? user?.id : "all"],
    queryFn: async () => {
      // For org admins, scope to their own organization
      let viewerOrgId: string | null = null;
      if (isOrgAdmin && user?.id) {
        const { data: ownRole } = await supabase
          .from('user_roles')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();
        viewerOrgId = ownRole?.organization_id || null;
      }

      let query = supabase
        .from('user_roles')
        .select(`
          *,
          organizations (
            name
          )
        `);

      if (isOrgAdmin && viewerOrgId) {
        query = query.eq('organization_id', viewerOrgId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching user roles:', error);
        throw error;
      }

      // Get profile data separately to avoid relationship issues
      const rolesWithProfiles = await Promise.all(
        (data || []).map(async (role) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .eq('user_id', role.user_id)
            .single();

          return {
            ...role,
            profiles: {
              id: profile?.id || null,
              first_name: profile?.first_name || null,
              last_name: profile?.last_name || null
            }
          };
        })
      );

      return rolesWithProfiles;
    },
    enabled: !!user?.id
  });

  // Extract unique organizations and roles for filters
  const organizations = sortOrganisations([...new Set(userRoles?.map(role => role.organizations?.name).filter((name): name is string => !!name))], name => name);
  const roles = [...new Set(userRoles?.map(role => role.role))];

  // Filter the roles based on selected filters
  const filteredRoles = userRoles?.filter(role => {
    const matchesOrg = selectedOrg === "all" || role.organizations?.name === selectedOrg;
    const matchesRole = selectedRole === "all" || role.role === selectedRole;
    return matchesOrg && matchesRole;
  });

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.userRoles} />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
            
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              {!isOrgAdmin && (
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org} value={org || ''}>
                      {org}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}

              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[120px] sm:w-[150px]">Name</TableHead>
                      <TableHead className="min-w-[140px] sm:w-[200px] hidden sm:table-cell">Organization</TableHead>
                      <TableHead className="min-w-[100px] sm:w-[120px]">Role</TableHead>
                      <TableHead className="min-w-[160px] sm:w-[180px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRoles?.map((role) => (
                      <UserRoleRow
                        key={role.id}
                        role={role as any}
                        restrictToOrgAdmin={isOrgAdmin}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
